import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { errorText, useApi } from '../../lib/api';
import { analyzeChain, enumerateAssignments } from '../../lib/story/chainTree';
import type { BeatChainDef } from '../../lib/story/types';
import { queueDevBeat, simulateBeats, type SimulateResult } from '../../lib/storyApi';
import { inputCls, smallBtn } from '../forms/fields';
import { TONE_COLORS, parseContext } from '../../lib/story/ui';
import { ToneMixBar } from './StoryBits';

const pct = (v: number | undefined) => (v === undefined ? '–' : `${Math.round(v * 100)}%`);

/** "Not available yet" for a 404/405 from an endpoint another stream is still building. */
function apiProblem(e: unknown, endpoint: string, owner: string) {
  const msg = errorText(e);
  return /HTTP 40[45]|Not Found|Method Not Allowed/i.test(msg)
    ? `Not available yet: this needs the ${owner} API (${endpoint}).`
    : msg;
}

/** Each hidden var's values (bool → true/false, enum → its values, int → what the cases pick). */
function varValues(chain: BeatChainDef, id: string): string[] {
  const v = (chain.hidden ?? []).find((h) => h.id === id);
  if (!v) return [];
  if (v.type === 'bool' || !v.type) return ['true', 'false'];
  if (v.type === 'enum') return v.values ?? [];
  return [...new Set((v.cases ?? []).flatMap((k) => (k.pick ?? []).map((p) => p.value ?? '')))];
}

/**
 * Simulate rolls (engine.html §5): 1 · roll one ticket (with pinned vars), 2 · play an option from it (the outcome was fixed when the
 * ticket was rolled), 3 · simulate 1000 tickets for outcome shares, reach, tell accuracy and dead rows. All rolls run on the server
 * (`POST /admin/beats/simulate`, the real shared-cs BeatRoller); until a button is pressed the panel shows the editor's expected values.
 * "Queue in my dev raid" calls `POST /dev/beats/queue` (dev only, tester tools) for the signed-in account's player.
 */
export function SimulatePanel({ chain }: { chain: BeatChainDef }) {
  const { client } = useApi();
  const [pins, setPins] = useState<Record<string, string>>({});
  const [contextText, setContextText] = useState('');
  const [seed, setSeed] = useState(1);
  const [picked, setPicked] = useState<string | null>(null);
  const context = parseContext(contextText);
  const pinned = Object.fromEntries(Object.entries(pins).filter(([, v]) => v !== ''));
  const request = (n: number) => ({ chain, n, pin: pinned, context, seed });

  const one = useMutation({ mutationFn: () => simulateBeats(client, request(1)) });
  const many = useMutation({ mutationFn: () => simulateBeats(client, request(1000)) });
  const queue = useMutation({
    mutationFn: () => queueDevBeat(client, { chainId: chain.id ?? '', pin: pinned }),
  });

  // Expected values from the editor, shown until the server answers.
  const expected = analyzeChain(chain, { pins: pinned, context });
  const rows = enumerateAssignments(chain, { pins: pinned, context });
  const share = (id: string, value: string) =>
    rows.filter((r) => r.vars[id] === value).reduce((s, r) => s + r.p, 0);
  const root = chain.beats?.[chain.root ?? ''];
  const rootOptions = root?.choice?.options ?? [];
  const sample = one.data?.simulation.sample ?? undefined;
  const pickedRoll = picked && sample ? sample.rolls?.[`${chain.root}:${picked}`] : undefined;
  const pickedOutcome = pickedRoll?.o ? chain.outcomes?.[pickedRoll.o] : undefined;

  return (
    <section
      className="rounded border-2 border-ink bg-white p-2 text-xs"
      aria-label="Simulate rolls"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold">Simulate rolls · {chain.id}</h3>
        <label className="flex items-center gap-1">
          seed
          <input
            aria-label="Seed"
            type="number"
            className={`${inputCls} w-20`}
            value={seed}
            onChange={(e) => setSeed(Number(e.target.value) || 1)}
          />
        </label>
      </div>

      <h4 className="mt-2 font-semibold">1 · Roll a ticket</h4>
      <div className="flex flex-wrap gap-2">
        {[
          ...(chain.hidden ?? []).map((h) => h.id ?? ''),
          ...(chain.tells ?? []).map((t) => t.id ?? ''),
        ].map((id) => {
          const isTell = (chain.tells ?? []).some((t) => t.id === id);
          const values = isTell ? ['true', 'false'] : varValues(chain, id);
          return (
            <label key={id} className="flex items-center gap-1">
              pin <span className="font-mono">{id}</span>
              <select
                aria-label={`Pin ${id}`}
                className={`${inputCls} w-auto`}
                value={pins[id] ?? ''}
                onChange={(e) => setPins({ ...pins, [id]: e.target.value })}
              >
                <option value="">random</option>
                {values.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
          );
        })}
      </div>
      <label className="mt-1 block">
        <span className="font-semibold">Context</span> (folded facts, one{' '}
        <span className="font-mono">fact=value</span> a line, e.g.{' '}
        <span className="font-mono">team.size=3</span>,{' '}
        <span className="font-mono">ledger.tag(killed_innocent)=true</span>)
        <textarea
          aria-label="Context"
          className={`${inputCls} h-12 font-mono`}
          value={contextText}
          onChange={(e) => setContextText(e.target.value)}
        />
      </label>
      <button
        type="button"
        className={`${smallBtn} mt-1`}
        disabled={one.isPending}
        onClick={() => {
          setPicked(null);
          one.mutate();
        }}
      >
        Roll ticket
      </button>
      {one.error && (
        <p role="alert" className="text-blam">
          {apiProblem(one.error, 'POST /admin/beats/simulate', 'S1-ENG')}
        </p>
      )}
      {one.data && <Verdict result={one.data} />}
      {sample && (
        <ul className="mt-1 font-mono" aria-label="Rolled ticket">
          {Object.entries(sample.vars ?? {}).map(([k, v]) => (
            <li key={k}>
              {k} ={' '}
              {(chain.tells ?? []).some((t) => t.id === k)
                ? v === 'true'
                  ? 'shown'
                  : 'hidden'
                : v}
            </li>
          ))}
        </ul>
      )}

      <h4 className="mt-2 font-semibold">2 · Play an option</h4>
      <div className="flex flex-wrap gap-1">
        {rootOptions.map((o) => (
          <button
            key={o.id}
            type="button"
            className={`${smallBtn} ${picked === o.id ? 'bg-ink text-newsprint' : ''}`}
            disabled={!sample}
            onClick={() => setPicked(o.id ?? '')}
          >
            {o.id} {o.label}
          </button>
        ))}
      </div>
      <p className="text-bruise">
        Pick an option. The outcome was fixed when the ticket was rolled.
      </p>
      {picked && (
        <p data-testid="played-outcome">
          {pickedRoll?.o ? (
            <>
              → <span className="font-mono">{pickedRoll.o}</span>{' '}
              {pickedOutcome?.tone && (
                <span
                  className="rounded px-1"
                  style={{
                    background: TONE_COLORS[pickedOutcome.tone as keyof typeof TONE_COLORS],
                  }}
                >
                  {pickedOutcome.tone}
                </span>
              )}{' '}
              · effects: {(pickedOutcome?.effects ?? []).map((e) => e.type).join(', ') || '–'}
              {!!pickedRoll.items?.length && (
                <> · items {pickedRoll.items.map((x) => `${x.quantity}× ${x.itemId}`).join(', ')}</>
              )}
              {!!pickedRoll.home?.length && (
                <> · home {pickedRoll.home.map((x) => `${x.quantity}× ${x.itemId}`).join(', ')}</>
              )}
              {!!pickedRoll.wf && <> · windfall {pickedRoll.wf}</>}
            </>
          ) : pickedRoll?.u != null ? (
            <>decided on the device by raid facts (u = {pickedRoll.u})</>
          ) : (
            <>not in the roll sheet</>
          )}
        </p>
      )}

      <h4 className="mt-2 font-semibold">3 · Simulate 1000 tickets</h4>
      <button
        type="button"
        className={smallBtn}
        disabled={many.isPending}
        onClick={() => many.mutate()}
      >
        Run 1000
      </button>
      {many.error && (
        <p role="alert" className="text-blam">
          {apiProblem(many.error, 'POST /admin/beats/simulate', 'S1-ENG')}
        </p>
      )}
      {many.data ? (
        <SimStats result={many.data} chain={chain} />
      ) : (
        <div className="mt-1" data-testid="expected-values">
          <p className="text-bruise">Expected values until you press a button:</p>
          <ul className="font-mono">
            {(chain.hidden ?? []).map((h) =>
              varValues(chain, h.id ?? '').map((v) => (
                <li key={`${h.id}=${v}`}>
                  {h.id} = {v}: {pct(share(h.id ?? '', v))}
                </li>
              )),
            )}
            {(chain.tells ?? []).map((t) => {
              const shown = share(t.id ?? '', 'true');
              const both = rows
                .filter((r) => r.vars[t.id ?? ''] === 'true' && r.vars[t.of ?? ''] === 'true')
                .reduce((s, r) => s + r.p, 0);
              const hiddenTrue = rows
                .filter((r) => r.vars[t.id ?? ''] === 'false' && r.vars[t.of ?? ''] === 'true')
                .reduce((s, r) => s + r.p, 0);
              return (
                <li key={t.id}>
                  {t.id} shown {pct(shown)} · P({t.of} | shown){' '}
                  {pct(shown ? both / shown : undefined)} · P({t.of} | not shown){' '}
                  {pct(1 - shown ? hiddenTrue / (1 - shown) : undefined)}
                </li>
              );
            })}
            <li>roll sheet {expected.rollSheetEntries} entries</li>
          </ul>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-newsprint pt-2">
        <button
          type="button"
          className={smallBtn}
          disabled={queue.isPending || !chain.id}
          onClick={() => queue.mutate()}
        >
          Queue in my dev raid
        </button>
        <span className="text-bruise">
          Dev server only (tester tools). Queues the <em>published</em> chain by id, with the pins
          above, into your next ticket.
        </span>
        {queue.isSuccess && <span className="font-semibold text-benday">Queued.</span>}
        {queue.error && (
          <span role="alert" className="text-blam">
            {apiProblem(queue.error, 'POST /dev/beats/queue', 'S1-ENG')}
          </span>
        )}
      </div>
    </section>
  );
}

/** The server validator's verdict on the draft (errors refuse a publish) and its warnings. */
function Verdict({ result }: { result: SimulateResult }) {
  if (!result.errors.length && !result.warnings.length && !result.simulation.error) return null;
  return (
    <ul className="mt-1" aria-label="Server verdict">
      {result.simulation.error && <li className="text-blam">{result.simulation.error}</li>}
      {result.errors.map((e) => (
        <li key={e} className="text-blam">
          {e}
        </li>
      ))}
      {result.warnings.map((w) => (
        <li key={w} className="text-bruise">
          ⚠ {w}
        </li>
      ))}
    </ul>
  );
}

function SimStats({ result, chain }: { result: SimulateResult; chain: BeatChainDef }) {
  const sim = result.simulation;
  return (
    <div className="mt-1" data-testid="sim-stats">
      <Verdict result={result} />
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <p className="font-semibold">Vars ({sim.tickets ?? 0} tickets)</p>
          <ul className="font-mono">
            {Object.entries(sim.vars ?? {}).flatMap(([id, values]) =>
              Object.entries(values).map(([v, p]) => (
                <li key={`${id}=${v}`}>
                  {id} = {v}: {pct(p)}
                </li>
              )),
            )}
            {(sim.tells ?? []).map((t) => {
              const of = t.of || (chain.tells ?? []).find((x) => x.id === t.id)?.of || '?';
              return (
                <li key={t.id}>
                  {t.id} shown {pct(t.shown)} · P({of} | shown) {pct(t.givenShown?.['true'])} · P(
                  {of} | not shown) {pct(t.givenHidden?.['true'])}
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <p className="font-semibold">Outcomes per option</p>
          <ul className="font-mono">
            {(sim.options ?? []).map((o) => (
              <li key={o.key}>
                {o.key}
                {o.raidFact ? ' (raid facts)' : ''} ─{' '}
                {Object.entries(o.outcomes ?? {})
                  .map(([id, p]) => `${id} ${pct(p)}`)
                  .join(' · ')}
              </li>
            ))}
          </ul>
          <p>
            roll sheet {sim.rollSheetSize ?? 0} · dead rows:{' '}
            {sim.deadRows?.length ? (
              <span className="text-blam">{sim.deadRows.join(', ')}</span>
            ) : (
              'none'
            )}
          </p>
        </div>
      </div>
      {sim.tone && (
        <ToneMixBar
          label="Tone mix (simulated)"
          mix={{
            disaster: sim.tone.disaster ?? 0,
            bad: sim.tone.bad ?? 0,
            mixed: sim.tone.mixed ?? 0,
            good: sim.tone.good ?? 0,
            windfall: sim.tone.windfall ?? 0,
          }}
        />
      )}
    </div>
  );
}
